import React, { useCallback, useEffect, useMemo } from 'react';

import {
    calculateHourMinFormat,
    formatDate,
    sortableHeader
} from 'pages/reports/components/ReportFunctions';
import { ReportToolTip } from 'pages/reports/components/StudentMetricsDisplay/StudentMetricsDisplay';
import { createSortChangeHandler, GridSortChangeEvent } from 'pages/reports/components/sortUtils';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { Card, Col, Container, Row, Table } from 'react-bootstrap';
import { orderBy, SortDescriptor } from 'utils/dataQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { ReportSection } from 'apis/ReportApi';
import { buildExportFileName } from 'utils';
import { useSuperState } from 'hooks';
import { CSVLink } from 'react-csv';

import 'pages/reports/components/Report71/Report71.scss';

interface Report71Props {
    mode?: string;
    reportSections: ReportSection[];
}

interface StudentRow {
    attained: number;
    avgAttemptsPerQuestion: number;
    coverage: number;
    lastAttempt: Date | null;
    recent: number;
    StudentName: string;
    StudentProfileId: number;
    totalAttempts: number;
    totalTimeSpentMinutes: number;
}

interface SummaryMetrics {
    avgAttained: number;
    avgAttemptsPerStudent: number;
    avgCoverage: number;
    avgRecentPerformance: number;
    avgTimePerStudentMinutes: number;
    totalStudents: number;
}

const STUDENT_DETAIL_REPORT_ID = '72';
const MAX_DISPLAY_ROWS = 500;

const navigateToStudentDetail = (studentProfileId: number) => {

    const url = new URL(window.location.href);
    url.pathname = `/reports/${STUDENT_DETAIL_REPORT_ID}`;
    url.searchParams.set('ClassroomStudents', studentProfileId.toString());
    window.location.href = url.toString();

};

export const Report71 = ({
    mode = 'Testing',
    reportSections
}: Report71Props) => {

    const [state, setState] = useSuperState({
        exportFileName: 'StudentOverviewReport.csv',
        exportRows: [] as any[],
        sort: [] as SortDescriptor[],
        summaryMetrics: {
            avgAttained: 0,
            avgAttemptsPerStudent: 0,
            avgCoverage: 0,
            avgRecentPerformance: 0,
            avgTimePerStudentMinutes: 0,
            totalStudents: 0
        } as SummaryMetrics,
        tableRows: [] as StudentRow[]
    });

    const { exportFileName, exportRows, sort, summaryMetrics, tableRows } = state;

    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    const sortedData = useMemo(() => orderBy(tableRows, sort), [tableRows, sort]);
    const displayData = useMemo(() => sortedData.slice(0, MAX_DISPLAY_ROWS), [sortedData]);

    // Aggregate raw question-attempt data into per-student rows and summary metrics
    const aggregateData = useCallback((data: any[]) => {

        const allQuestionIds = new Set(data.map((d: any) => d.QuestionId));
        const totalQuestionsInScope = allQuestionIds.size;

        // Group attempts by student
        const studentMap: Record<number, any[]> = {};
        data.forEach((item: any) => {

            const id = item.StudentId;
            if (!studentMap[id]) studentMap[id] = [];
            studentMap[id].push(item);

        });

        const studentRows: StudentRow[] = Object.entries(studentMap).map(([, attempts]) => {

            const studentId = attempts[0].StudentId;
            const studentName = `${(attempts[0].FirstName || '').trim()} ${(attempts[0].LastName || '').trim()}`.trim();

            // Coverage: unique questions attempted at least once
            const uniqueQuestionsAttempted = new Set(attempts.map((a: any) => a.QuestionId));
            const coverage = totalQuestionsInScope > 0
                ? (uniqueQuestionsAttempted.size / totalQuestionsInScope) * 100
                : 0;

            // Attained: questions answered correctly at least once
            const correctQuestionIds = new Set(
                attempts.filter((a: any) => a.Correct?.Share === '1').map((a: any) => a.QuestionId)
            );
            const attained = totalQuestionsInScope > 0
                ? (correctQuestionIds.size / totalQuestionsInScope) * 100
                : 0;

            // Recent: for each question, find the most recent attempt and check if correct
            const questionLatestMap: Record<number, any> = {};
            attempts.forEach((a: any) => {

                const qId = a.QuestionId;
                if (!questionLatestMap[qId] || new Date(a.TimeStamp) > new Date(questionLatestMap[qId].TimeStamp)) {

                    questionLatestMap[qId] = a;

                }

            });
            const recentCorrectCount = Object.values(questionLatestMap).filter((a: any) => a.Correct?.Share === '1').length;
            const recent = totalQuestionsInScope > 0
                ? (recentCorrectCount / totalQuestionsInScope) * 100
                : 0;

            // Total attempts
            const totalAttempts = attempts.length;

            // Avg attempts per question (against total questions in scope, not just attempted)
            const avgAttemptsPerQuestion = totalQuestionsInScope > 0
                ? totalAttempts / totalQuestionsInScope
                : 0;

            // Total time spent (in minutes) — deduplicate by ContentAccessId since
            // Testtotalminutesspent is the same for every question row within a test attempt
            const seenAccessIds = new Set<number>();
            let totalTimeSpentMinutes = 0;
            attempts.forEach((a: any) => {

                if (a.ContentAccessId && !seenAccessIds.has(a.ContentAccessId)) {

                    seenAccessIds.add(a.ContentAccessId);
                    totalTimeSpentMinutes += a.Testtotalminutesspent || 0;

                }

            });

            // Last attempt date
            const lastAttempt = attempts.reduce((latest: Date | null, a: any) => {

                if (!a.TimeStamp) return latest;
                const d = new Date(a.TimeStamp);
                return (!latest || d > latest) ? d : latest;

            }, null);

            return {
                attained,
                avgAttemptsPerQuestion,
                coverage,
                lastAttempt,
                recent,
                StudentName: studentName,
                StudentProfileId: studentId,
                totalAttempts,
                totalTimeSpentMinutes
            };

        });

        // Summary metrics
        const totalStudents = studentRows.length;
        const avgCoverage = totalStudents > 0
            ? studentRows.reduce((sum, s) => sum + s.coverage, 0) / totalStudents
            : 0;
        const avgAttained = totalStudents > 0
            ? studentRows.reduce((sum, s) => sum + s.attained, 0) / totalStudents
            : 0;
        const avgRecentPerformance = totalStudents > 0
            ? studentRows.reduce((sum, s) => sum + s.recent, 0) / totalStudents
            : 0;
        const totalAllAttempts = studentRows.reduce((sum, s) => sum + s.totalAttempts, 0);
        const avgAttemptsPerStudent = totalStudents > 0 ? totalAllAttempts / totalStudents : 0;
        const totalAllTime = studentRows.reduce((sum, s) => sum + s.totalTimeSpentMinutes, 0);
        const avgTimePerStudentMinutes = totalStudents > 0 ? totalAllTime / totalStudents : 0;

        setState({
            summaryMetrics: {
                avgAttained,
                avgAttemptsPerStudent,
                avgCoverage,
                avgRecentPerformance,
                avgTimePerStudentMinutes,
                totalStudents
            }
        });

        setState({ tableRows: studentRows });

    }, []);

    // Sorting
    const getSortableValue = useCallback((item: any, field?: string) => {

        if (!field) return null;
        if (field === 'lastAttempt') return item[field] ? new Date(item[field]).getTime() : null;
        return item[field];

    }, []);

    const handleSortChange = useCallback((event: GridSortChangeEvent) => {

        createSortChangeHandler(event, sort, val => setState({ sort: val }), getSortableValue);

    }, [sort, getSortableValue]);

    // Export
    const buildExport = useCallback(() => sortedData.map((row: StudentRow) => ({

        [formatMessage({ id: 'attained.704661b2', defaultMessage: 'Attained' })]: `${Math.round(row.attained)}%`,
        [formatMessage({ id: 'avg_attempts_question.64637c60', defaultMessage: 'Avg Attempts / Question' })]: row.avgAttemptsPerQuestion.toFixed(1),
        [formatMessage({ id: 'coverage.2e5a02a7', defaultMessage: 'Coverage' })]: `${Math.round(row.coverage)}%`,
        [formatMessage({ id: 'last_attempt.23c63d91', defaultMessage: 'Last Attempt' })]: row.lastAttempt ? formatDate(row.lastAttempt) : '---',
        [formatMessage({ id: 'recent.f6fba114', defaultMessage: 'Recent' })]: `${Math.round(row.recent)}%`,
        [formatMessage({ id: 'student_name.f4347b21', defaultMessage: 'Student Name' })]: row.StudentName,
        [formatMessage({ id: 'total_attempts.0d55f966', defaultMessage: 'Total Attempts' })]: row.totalAttempts,
        [formatMessage({ id: 'total_time_spent.88b400d6', defaultMessage: 'Total Time Spent' })]: calculateHourMinFormat(Math.round(row.totalTimeSpentMinutes))

    })), [sortedData, formatMessage]);

    const handleExportCSV = useCallback(() => {

        const reportName = reportSections[0]?.Title || 'StudentOverviewReport';
        const fileName = buildExportFileName('Report', reportName, 'csv');
        setState({ exportFileName: fileName });
        setState({ exportRows: buildExport() });
        setTimeout(() => document.getElementById('csvLink')?.click(), 0);

    }, [reportSections, buildExport]);

    // Initialize on mount
    useEffect(() => {

        const allData = reportSections[0]?.Components[0]?.Results?.Data ?? [];
        const rawData = allData.filter((d: any) => !d.ContentAccessTypeDisplayName || d.ContentAccessTypeDisplayName === mode);
        aggregateData(rawData);

    }, [aggregateData, reportSections, mode]);

    // Toast for truncated results
    useEffect(() => {

        if (tableRows.length > MAX_DISPLAY_ROWS) {

            createToast(
                formatMessage(
                    { id: 'showing_first_shown_records_of_total_ple.18a484b7', defaultMessage: 'Showing first {shown} records of {total}. Please refine your filters to narrow the results.' },
                    { shown: MAX_DISPLAY_ROWS, total: tableRows.length }
                ),
                'warning'
            );

        }

    }, [tableRows]);

    // Summary card definitions
    const summaryCards = useMemo(() => [
        {
            label: formatMessage({ id: 'total_students.54df8f15', defaultMessage: 'Total Students' }),
            tooltip: formatMessage({ id: 'total_number_of_students_included_within.f5520ea1', defaultMessage: 'Total number of students included within the selected scope' }),
            value: summaryMetrics.totalStudents.toString()
        },
        {
            label: formatMessage({ id: 'avg_question_coverage.aa69d34c', defaultMessage: 'Avg Question Coverage' }),
            tooltip: formatMessage({ id: 'average_percentage_of_questions_that_stu.41799141', defaultMessage: 'Average percentage of questions that students have attempted at least once' }),
            value: `${Math.round(summaryMetrics.avgCoverage)}%`
        },
        {
            label: formatMessage({ id: 'avg_attained.8c0a67d4', defaultMessage: 'Avg Attained' }),
            tooltip: formatMessage({ id: 'average_percentage_of_questions_answered.5609839f', defaultMessage: 'Average percentage of questions answered correctly at least once per student' }),
            value: `${Math.round(summaryMetrics.avgAttained)}%`
        },
        {
            label: formatMessage({ id: 'avg_recent.b5774606', defaultMessage: 'Avg Recent' }),
            tooltip: formatMessage({ id: 'average_percentage_of_questions_where_ea.499851bf', defaultMessage: "Average percentage of questions where each student's most recent submitted attempt was correct" }),
            value: `${Math.round(summaryMetrics.avgRecentPerformance)}%`
        },
        {
            label: formatMessage({ id: 'avg_attempts_student.f3df6812', defaultMessage: 'Avg Attempts / Student' }),
            tooltip: formatMessage({ id: 'average_number_of_submitted_attempts_per.c023824c', defaultMessage: 'Average number of submitted attempts per student within the selected scope' }),
            value: Math.round(summaryMetrics.avgAttemptsPerStudent).toString()
        },
        {
            label: formatMessage({ id: 'avg_time_student.ef321afd', defaultMessage: 'Avg Time / Student' }),
            tooltip: formatMessage({ id: 'average_total_time_spent_per_student_acr.0e37c32e', defaultMessage: 'Average total time spent per student across submitted attempts within the selected scope' }),
            value: calculateHourMinFormat(Math.round(summaryMetrics.avgTimePerStudentMinutes))
        }
    ], [summaryMetrics, formatMessage]);

    return (
        <>
            {/* Header Summary — Student Overview */}
            <Container fluid className="px-0 mb-4">
                <h5 className="fw-bold mb-3">
                    <FormattedMessage defaultMessage="Student Overview" id="student_overview.93a3b769" />
                </h5>
                <Row className="g-3 mx-0">
                    {summaryCards.map(metric => (
                        <Col key={metric.label} lg="2" md="4" sm="4" xs="6">
                            <Card className="p-3 shadow-sm h-100 text-center">
                                <ReportToolTip id={`summary-${metric.label}`} tooltip={metric.tooltip} trigger="hover">
                                    <div className="fw-semibold text-secondary mb-2" style={{ fontSize: '0.85rem' }}>
                                        {metric.label}
                                    </div>
                                    <div className="fw-bold fs-3">
                                        {metric.value}
                                    </div>
                                </ReportToolTip>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>

            {/* Table */}
            <Card>
                <CSVLink
                    className="d-none"
                    data={exportRows}
                    filename={exportFileName}
                    id="csvLink"
                    type="button"
                >
                </CSVLink>
                <div className="p-3 d-flex justify-content-end">
                    <GMetrixButton onClick={handleExportCSV} variant="primary">
                        <FormattedMessage defaultMessage="Export to CSV" id="export_to_csv.e1de1359" />
                    </GMetrixButton>
                </div>
                <div style={{ maxHeight: 'calc(100vh - 70px - 24px - 105px)', overflowX: 'auto', overflowY: 'auto' }}>
                    <Table bordered hover responsive className="kendo-table mb-0">
                        <thead>
                            <tr>
                                <th
                                    className="text-start"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'StudentName' }] })}
                                    style={{ cursor: 'pointer', left: 0, minWidth: 150, position: 'sticky', zIndex: 1 }}
                                >
                                    {sortableHeader(
                                        <span className="d-flex justify-content-start">
                                            <FormattedMessage defaultMessage="Student Name" id="student_name.f4347b21" />
                                        </span>,
                                        'StudentName',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'coverage' }] })}
                                    style={{ cursor: 'pointer', minWidth: 130 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-coverage" tooltip={formatMessage({ id: 'percentage_of_questions_attempted_at_lea.af8e7244', defaultMessage: 'Percentage of questions attempted at least once by the student' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Coverage" id="coverage.2e5a02a7" />
                                            </span>
                                        </ReportToolTip>,
                                        'coverage',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'attained' }] })}
                                    style={{ cursor: 'pointer', minWidth: 130 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-attained" tooltip={formatMessage({ id: 'percentage_of_questions_answered_correct.ba49ce93', defaultMessage: 'Percentage of questions answered correctly at least once by this student' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Attained" id="attained.704661b2" />
                                            </span>
                                        </ReportToolTip>,
                                        'attained',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'recent' }] })}
                                    style={{ cursor: 'pointer', minWidth: 130 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-recent" tooltip={formatMessage({ id: 'percentage_of_questions_where_the_studen.ebe4814e', defaultMessage: "Percentage of questions where the student's most recent submitted attempt was correct within the selected scope" })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Recent" id="recent.f6fba114" />
                                            </span>
                                        </ReportToolTip>,
                                        'recent',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'totalAttempts' }] })}
                                    style={{ cursor: 'pointer', minWidth: 140 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-totalAttempts" tooltip={formatMessage({ id: 'total_number_of_submitted_question_attem.73ab6a03', defaultMessage: 'Total number of submitted question attempts by the student within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Total Attempts" id="total_attempts.0d55f966" />
                                            </span>
                                        </ReportToolTip>,
                                        'totalAttempts',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'avgAttemptsPerQuestion' }] })}
                                    style={{ cursor: 'pointer', minWidth: 180 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-avgAttempts" tooltip={formatMessage({ id: 'average_number_of_submitted_attempts_per.85b24416', defaultMessage: 'Average number of submitted attempts per question for this student within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Avg Attempts / Question" id="avg_attempts_question.64637c60" />
                                            </span>
                                        </ReportToolTip>,
                                        'avgAttemptsPerQuestion',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'totalTimeSpentMinutes' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-totalTime" tooltip={formatMessage({ id: 'total_time_spent_across_all_submitted_at.83739188', defaultMessage: 'Total time spent across all submitted attempts by the student within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Total Time Spent" id="total_time_spent.88b400d6" />
                                            </span>
                                        </ReportToolTip>,
                                        'totalTimeSpentMinutes',
                                        sort
                                    )}
                                </th>
                                <th
                                    className="text-center"
                                    onClick={() => handleSortChange({ sort: [{ dir: 'asc', field: 'lastAttempt' }] })}
                                    style={{ cursor: 'pointer', minWidth: 150 }}
                                >
                                    {sortableHeader(
                                        <ReportToolTip id="col-lastAttempt" tooltip={formatMessage({ id: 'most_recent_submitted_attempt_date_for_t.fc1d64da', defaultMessage: 'Most recent submitted attempt date for the student within the selected scope' })} trigger="hover">
                                            <span className="d-flex justify-content-center">
                                                <FormattedMessage defaultMessage="Last Attempt" id="last_attempt.23c63d91" />
                                            </span>
                                        </ReportToolTip>,
                                        'lastAttempt',
                                        sort
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayData.map((student: StudentRow, rowIndex: number) => (
                                <tr key={`${student.StudentProfileId}-${rowIndex}`}>
                                    <td style={{ backgroundColor: 'white', left: 0, overflowX: 'hidden', position: 'sticky', zIndex: 1 }}>
                                        <span
                                            onClick={() => navigateToStudentDetail(student.StudentProfileId)}
                                            style={{ color: '#0066cc', cursor: 'pointer', textDecoration: 'underline' }}
                                            title={formatMessage({ id: 'view_student_details.66d3a582', defaultMessage: 'View student details' })}
                                        >
                                            {student.StudentName}
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        {Math.round(student.coverage)}
                                        %
                                    </td>
                                    <td className="text-center">
                                        {Math.round(student.attained)}
                                        %
                                    </td>
                                    <td className="text-center">
                                        {Math.round(student.recent)}
                                        %
                                    </td>
                                    <td className="text-center">
                                        {student.totalAttempts}
                                    </td>
                                    <td className="text-center">
                                        {student.avgAttemptsPerQuestion.toFixed(1)}
                                    </td>
                                    <td className="text-center">
                                        {calculateHourMinFormat(Math.round(student.totalTimeSpentMinutes))}
                                    </td>
                                    <td className="text-center">
                                        {student.lastAttempt ? formatDate(student.lastAttempt) : '---'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card>
        </>
    );

};